import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  useEffect,
} from 'react';
import {
  Paper,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Box,
  Typography,
  Popper,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';

export interface ProductSearchBarRef {
  focus: () => void;
  clear: () => void;
  getValue: () => string;
  inputElement: HTMLInputElement | null;
}

interface ProductSearchBarProps {
  onSearchSubmit: (searchTerm: string) => Promise<void>;
  isQuantityModeActive: boolean;
  quantityInputValue: string;
  onToggleQuantityMode: () => void;
  numpadValue?: string;
  onNumpadValueConsumed?: () => void;
  /**
   * Server-side autocomplete: min 3 karakter girilince çağrılır (debounced).
   * Ürün listesi döndürür (raw API response).
   */
  onAutocompleteSearch?: (term: string) => Promise<any[]>;
  /**
   * Autocomplete listesinden bir ürün seçildiğinde çağrılır.
   */
  onAutocompleteSelect?: (product: any) => Promise<void>;
}

/**
 * ✅ PERFORMANS OPTİMİZASYONU:
 *
 * - Input değeri LOCAL STATE'te tutuluyor → sadece bu component render oluyor
 * - Parent'a SADECE Enter veya Ara butonuna basılınca haber veriliyor
 * - Barkod okuyucu hızında çalışıyor!
 *
 * ✅ AUTOCOMPLETE:
 * - 3+ karakter manuel girişten sonra 400ms debounce ile server-side arama
 * - Barkod okuyucu tespiti: <50ms aralıklı input → autocomplete atlanır
 * - Dropdown seçimi doğrudan sepete ekler (yeniden arama yapmadan)
 */
const ProductSearchBar = forwardRef<ProductSearchBarRef, ProductSearchBarProps>(
  (
    {
      onSearchSubmit,
      isQuantityModeActive,
      quantityInputValue,
      onToggleQuantityMode,
      numpadValue,
      onNumpadValueConsumed,
      onAutocompleteSearch,
      onAutocompleteSelect,
    },
    ref
  ) => {
    const { t } = useTranslation('salesSystem');
    const [localSearchTerm, setLocalSearchTerm] = useState('');
    const [autocompleteResults, setAutocompleteResults] = useState<any[]>([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [isLoadingAC, setIsLoadingAC] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    const inputRef = useRef<HTMLInputElement>(null);
    const anchorRef = useRef<HTMLDivElement>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    /** Barkod okuyucu tespiti: son iki tuş arasındaki ms */
    const lastKeyTimeRef = useRef<number>(0);
    /** Barkod okuyucu devrede ise autocomplete'i geçici olarak kapat */
    const suppressACRef = useRef<boolean>(false);
    const suppressACTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    /** Mevcut autocomplete çağrısının fetch ID'si (race condition önleme) */
    const acFetchIdRef = useRef<number>(0);

    useImperativeHandle(ref, () => ({
      focus: () => inputRef.current?.focus(),
      clear: () => {
        setLocalSearchTerm('');
        closeDropdown();
      },
      getValue: () => localSearchTerm,
      inputElement: inputRef.current,
    }));

    const closeDropdown = useCallback(() => {
      setShowDropdown(false);
      setAutocompleteResults([]);
      setHighlightedIndex(-1);
      clearTimeout(debounceRef.current);
    }, []);

    // Arama submit
    const handleSubmit = useCallback(() => {
      const term = localSearchTerm.trim();
      if (term) {
        closeDropdown();
        onSearchSubmit(term);
        setLocalSearchTerm('');
      }
    }, [localSearchTerm, onSearchSubmit, closeDropdown]);

    // Enter / Arrow key handler
    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (showDropdown && autocompleteResults.length > 0) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex(i =>
              i < autocompleteResults.length - 1 ? i + 1 : 0
            );
            return;
          }
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex(i =>
              i > 0 ? i - 1 : autocompleteResults.length - 1
            );
            return;
          }
          if (e.key === 'Enter' && highlightedIndex >= 0) {
            e.preventDefault();
            const selected = autocompleteResults[highlightedIndex];
            handleAutocompleteItemSelect(selected);
            return;
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            closeDropdown();
            return;
          }
        }

        if (e.key === 'Enter') {
          e.preventDefault();
          handleSubmit();
        }
      },
      [showDropdown, autocompleteResults, highlightedIndex, handleSubmit, closeDropdown]
    );

    // Autocomplete item seçme
    const handleAutocompleteItemSelect = useCallback(
      async (product: any) => {
        closeDropdown();
        setLocalSearchTerm('');
        if (onAutocompleteSelect) {
          await onAutocompleteSelect(product);
        }
        inputRef.current?.focus();
      },
      [onAutocompleteSelect, closeDropdown]
    );

    // Input değişikliği
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocalSearchTerm(value);
        setHighlightedIndex(-1);

        if (!onAutocompleteSearch) return;

        // Barkod okuyucu tespiti
        const now = Date.now();
        const interval = now - lastKeyTimeRef.current;
        lastKeyTimeRef.current = now;

        if (interval < 50) {
          // Çok hızlı input → barkod okuyucu
          suppressACRef.current = true;
          clearTimeout(suppressACTimerRef.current);
          // Son hızlı tuştan 600ms sonra baskıyı kaldır
          suppressACTimerRef.current = setTimeout(() => {
            suppressACRef.current = false;
          }, 600);
        }

        clearTimeout(debounceRef.current);

        if (value.trim().length < 3) {
          setShowDropdown(false);
          setAutocompleteResults([]);
          return;
        }

        debounceRef.current = setTimeout(async () => {
          if (suppressACRef.current) return;

          const fetchId = ++acFetchIdRef.current;
          setIsLoadingAC(true);
          try {
            const results = await onAutocompleteSearch(value.trim());
            if (fetchId !== acFetchIdRef.current) return; // stale
            setAutocompleteResults(results.slice(0, 12));
            setShowDropdown(results.length > 0);
          } catch {
            if (fetchId !== acFetchIdRef.current) return;
            setShowDropdown(false);
          } finally {
            if (fetchId === acFetchIdRef.current) setIsLoadingAC(false);
          }
        }, 400);
      },
      [onAutocompleteSearch]
    );

    // Numpad'den gelen değer
    useEffect(() => {
      if (numpadValue === undefined || numpadValue === '') return;

      if (numpadValue === 'C') {
        setLocalSearchTerm('');
        closeDropdown();
      } else if (numpadValue === '<') {
        setLocalSearchTerm(prev => prev.slice(0, -1));
      } else if (numpadValue === 'Enter') {
        handleSubmit();
      } else if (/[0-9]/.test(numpadValue)) {
        setLocalSearchTerm(prev => prev + numpadValue);
      }
      onNumpadValueConsumed?.();
    }, [numpadValue]);

    // Cleanup
    useEffect(() => {
      return () => {
        clearTimeout(debounceRef.current);
        clearTimeout(suppressACTimerRef.current);
      };
    }, []);

    const displayName = (p: any) => p.name || p.Name || '';
    const displayCategory = (p: any) => p.categoryName || p.CategoryName || '';
    const displayQty = (p: any) => {
      const q = p.totalQuantity ?? p.TotalQuantity ?? p.quantity ?? p.Quantity;
      return q != null ? t('productSearchBar.stock', { count: q }) : '';
    };

    return (
      <ClickAwayListener onClickAway={closeDropdown}>
        <Paper elevation={1} sx={{ p: 1, mb: 1, bgcolor: 'background.paper', position: 'relative' }}>
          <Grid container spacing={1} alignItems="stretch">
            <Grid item xs ref={anchorRef}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={t('productSearchBar.searchPlaceholder')}
                value={localSearchTerm}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      {isLoadingAC ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SearchIcon />
                      )}
                    </InputAdornment>
                  ),
                  sx: { height: 48, bgcolor: '#e3f2fd' },
                }}
                inputRef={inputRef}
                autoFocus
              />

              {/* Autocomplete Dropdown */}
              <Popper
                open={showDropdown && autocompleteResults.length > 0}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                style={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
                modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
              >
                <Paper
                  elevation={4}
                  sx={{
                    width: '100%',
                    maxHeight: 320,
                    overflowY: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <List dense disablePadding>
                    {autocompleteResults.map((product, index) => (
                      <ListItemButton
                        key={product.id || product.Id || index}
                        selected={index === highlightedIndex}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        onMouseDown={e => {
                          e.preventDefault(); // input blur'ı engelle
                        }}
                        onClick={() => handleAutocompleteItemSelect(product)}
                        sx={{
                          py: 0.5,
                          '&.Mui-selected': {
                            bgcolor: 'primary.light',
                            '&:hover': { bgcolor: 'primary.light' },
                          },
                        }}
                      >
                        <ListItemText
                          primary={displayName(product)}
                          secondary={
                            <Box
                              component="span"
                              sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}
                            >
                              {displayCategory(product) && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {displayCategory(product)}
                                </Typography>
                              )}
                              {displayQty(product) && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color={
                                    (product.totalQuantity ?? product.TotalQuantity ?? 0) > 0
                                      ? 'success.main'
                                      : 'error.main'
                                  }
                                >
                                  {displayQty(product)}
                                </Typography>
                              )}
                            </Box>
                          }
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Paper>
              </Popper>
            </Grid>

            <Grid item>
              <Button
                variant={isQuantityModeActive ? 'contained' : 'outlined'}
                sx={{
                  height: 48,
                  minWidth: 48,
                  width: 48,
                  p: 0,
                  fontSize: '0.75rem',
                  lineHeight: 1.1,
                  borderColor: 'divider',
                  bgcolor: isQuantityModeActive ? 'warning.light' : undefined,
                }}
                onClick={onToggleQuantityMode}
              >
                {isQuantityModeActive ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ lineHeight: 1 }}>
                      {t('productSearchBar.quantity')}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ lineHeight: 1 }}>
                      [{quantityInputValue || '?'}]
                    </Typography>
                  </Box>
                ) : (
                  t('productSearchBar.quantity')
                )}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </ClickAwayListener>
    );
  }
);

export default ProductSearchBar;
